import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import {fmtDate} from './format';

/**
 * Kundeninfo aus dem Kundenstamm (Original `#custinfo` und
 * `renderCustomerInfo` 2808 / `printCustomerNote` 2831).
 *
 * Das Feld gehört dem Kunden, nicht dem Beleg: Was hier steht, erscheint auf
 * ALLEN Belegen dieses Kunden. Deshalb die Fußnote, der Marker „● geändert"
 * und die serverseitige Rückfrage, wenn ein großer Teil eines längeren Textes
 * verschwindet.
 *
 * @param {string} mode
 * @param {object} order
 * @param {?string} pending - vorgemerkter Text (`null` = unverändert).
 * @param {boolean} editable
 * @param {(value: ?string) => void} onChange
 */
export default function CustomerNote({mode, order, pending, editable, onChange}) {
    const [printing, setPrinting] = useState(false);

    // Der Druck darf erst starten, wenn der Druckbereich im Dokument steht —
    // sonst druckt der Browser eine leere Seite.
    useEffect(() => {
        if (!printing) {
            return undefined;
        }
        const t = window.setTimeout(() => {
            window.print();
            setPrinting(false);
        }, 50);
        return () => window.clearTimeout(t);
    }, [printing]);

    if (mode !== 'order' || !order || !order.payment) {
        return null;
    }

    const orig = order.payment.customerDescription || '';
    const wert = pending != null ? pending : orig;

    return (
        <div className="custinfo show">
            <div className="h">
                <span>
                    Kundeninfo (aus dem Kundenstamm)
                    <HelpTooltip
                        id="oe-custinfo"
                        placement="top"
                        size={0.7}
                        detail={<>
                            Dort steht oft die Bestellnummer des Kunden oder ein Hinweis zur Lieferung. Änderungen
                            gehen mit dem normalen Speichern in den Kundenstamm.
                        </>}
                    >
                        Bemerkungsfeld aus dem Kundenstamm — Änderungen gelten für alle Belege dieses Kunden.
                    </HelpTooltip>
                </span>
                <OeTip tip="Öffnet den Druckdialog des Browsers mit der Kundenbemerkung auf einem Briefkopf — dort lässt sie sich drucken oder als PDF sichern. Gedruckt wird der gespeicherte Stand.">
                    <button type="button" className="pxbtn" onClick={() => setPrinting(true)}>📄 als PDF</button>
                </OeTip>
            </div>

            {editable ? (
                <OeTip
                    placement="bottom"
                    tip={'Text des Kundenstamms. Änderungen sind erst vorgemerkt und gehen mit „Speichern" nach weclapp.'}
                >
                    <textarea
                        className="custedit"
                        spellCheck="false"
                        aria-label="Kundeninfo aus dem Kundenstamm"
                        placeholder="Kundeninfo aus dem Kundenstamm — hier bearbeiten…"
                        value={wert}
                        onChange={(e) => onChange(e.target.value === orig ? null : e.target.value)}
                    />
                </OeTip>
            ) : (
                <div className="body">{orig || <span className="muted">—</span>}</div>
            )}

            <div className="custfoot">
                <span className="muted">
                    ✎ Feld aus dem <b>Kundenstamm</b> — Änderungen wirken auf <b>alle Belege dieses Kunden</b>
                    {editable ? ' und werden mit dem normalen „Speichern" übernommen.' : '. Ohne Bearbeitungsrecht nur lesbar.'}
                </span>
                <span className="cdirty">{pending != null ? '● geändert' : ''}</span>
            </div>

            {printing ? <CustomerNotePrint order={order}/> : null}
        </div>
    );
}

/**
 * Druckbereich mit Briefkopf, Kundendaten und der Bemerkung (Original
 * `printCustomerNote`, 2831). Er hängt bewusst unmittelbar an `<body>`: Die
 * Druckregel blendet alle anderen direkten Kinder aus, sonst verschwände der
 * Bereich mit seinem Rahmen.
 */
function CustomerNotePrint({order}) {
    const p = order.payment || {};
    const a = order.recordAddress || {};

    const anschrift = [
        a.company,
        [a.street1, a.zipCode || a.zipcode, a.city].filter(Boolean).join(' '),
        a.countryCode,
    ].filter(Boolean);

    const kopf = [<b key="nr">Auftrag {order.number}</b>];
    if (order.orderDate) {
        kopf.push(`Auftragsdatum: ${fmtDate(order.orderDate)}`);
    }
    if (order.customerOrderNumber) {
        kopf.push(`Ihre Bestellnr.: ${order.customerOrderNumber}`);
    }
    if (order.shopNumber) {
        kopf.push(`Shop-Nr.: ${order.shopNumber}`);
    }

    return createPortal(
        <div id="oe-printArea" aria-hidden="true">
            <div className="pdoc">
                <div className="phead">
                    <div className="plogo">MOLOTOW</div>
                    <div className="pmeta">
                        {kopf.map((z, i) => <React.Fragment key={i}>{z}<br/></React.Fragment>)}
                        <span className="pmuted">gedruckt {fmtDate(Date.now())}</span>
                    </div>
                </div>
                <div className="pcust">
                    <div className="pcust-l">Kunde</div>
                    <div>
                        <b>{order.customerName || ''}</b><br/>
                        Kunden-Nr. {order.customerNumber || ''}
                        {p.vatId ? <><br/>USt-ID: {p.vatId}</> : null}
                        {anschrift.map((z, i) => <React.Fragment key={i}><br/>{z}</React.Fragment>)}
                        {order.customerEmail ? <><br/>{order.customerEmail}</> : null}
                    </div>
                </div>
                <h2 className="ptitle">Kundenbemerkung</h2>
                <div className="pbody">
                    {p.customerDescription ? p.customerDescription : <span className="pmuted">— keine —</span>}
                </div>
            </div>
        </div>,
        document.body,
    );
}
